import {
  size_default
} from "./chunk.SI24X5IH.js";
import {
  WebAwesomeElement,
  e,
  n,
  t
} from "./chunk.LVUMIIKQ.js";
import {
  ke
} from "./chunk.S3625EU6.js";
import {
  __decorateClass
} from "./chunk.SZE6ONST.js";

// src/events/select.ts
var WaSelectEvent = class extends Event {
  constructor(detail) {
    super("wa-select", { bubbles: true, cancelable: false, composed: true });
    this.detail = detail;
  }
};

// src/components/menu/menu.css
var menu_default = ":host {\n  display: block;\n  position: relative;\n  text-align: start;\n  background-color: var(--wa-color-surface-raised);\n  border: var(--wa-border-style) var(--wa-border-width-s) var(--wa-color-surface-border);\n  border-radius: var(--wa-border-radius-m);\n  padding: var(--wa-space-xs) 0;\n  overflow: auto;\n  overscroll-behavior: none;\n}\n\n::slotted(wa-divider) {\n  --spacing: var(--wa-space-xs);\n}\n";

// src/components/menu/menu.ts
var WaMenu = class extends WebAwesomeElement {
  constructor() {
    super(...arguments);
    this.size = "inherit";
  }
  connectedCallback() {
    super.connectedCallback();
    this.setAttribute("role", "menu");
  }
  handleClick(event) {
    const menuItemTypes = ["menuitem", "menuitemcheckbox"];
    const target = event.composedPath().find((el) => menuItemTypes.includes(el?.getAttribute?.("role") || ""));
    if (!target) return;
    const item = target;
    if (item.type === "checkbox") {
      item.checked = !item.checked;
    }
    this.dispatchEvent(new WaSelectEvent({ item }));
  }
  handleKeyDown(event) {
    if (event.key === "Enter" || event.key === " ") {
      const item = this.getCurrentItem();
      event.preventDefault();
      event.stopPropagation();
      item?.click();
    } else if (["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) {
      const items = this.getAllItems();
      const activeItem = this.getCurrentItem();
      let index = activeItem ? items.indexOf(activeItem) : 0;
      if (items.length > 0) {
        event.preventDefault();
        event.stopPropagation();
        if (event.key === "ArrowDown") {
          index++;
        } else if (event.key === "ArrowUp") {
          index--;
        } else if (event.key === "Home") {
          index = 0;
        } else if (event.key === "End") {
          index = items.length - 1;
        }
        if (index < 0) {
          index = items.length - 1;
        }
        if (index > items.length - 1) {
          index = 0;
        }
        this.setCurrentItem(items[index]);
        items[index].focus();
      }
    }
  }
  handleMouseDown(event) {
    const target = event.target;
    if (this.isMenuItem(target)) {
      this.setCurrentItem(target);
    }
  }
  handleSlotChange() {
    const items = this.getAllItems();
    if (items.length > 0) {
      this.setCurrentItem(items[0]);
    }
  }
  isMenuItem(item) {
    return item.tagName.toLowerCase() === "wa-menu-item" || ["menuitem", "menuitemcheckbox", "menuitemradio"].includes(item.getAttribute("role") ?? "");
  }
  /** @internal Gets all slotted menu items, ignoring dividers, headers, and other elements. */
  getAllItems() {
    return [...this.defaultSlot.assignedElements({ flatten: true })].filter((el) => {
      if (el.inert || !this.isMenuItem(el)) {
        return false;
      }
      return true;
    });
  }
  /**
   * @internal Gets the current menu item, which is the menu item that has `tabindex="0"` within the roving tab index.
   * The menu item may or may not have focus, but for keyboard interaction purposes it's considered the "active" item.
   */
  getCurrentItem() {
    return this.getAllItems().find((i) => i.getAttribute("tabindex") === "0");
  }
  /**
   * @internal Sets the current menu item to the specified element. This sets `tabindex="0"` on the target element and
   * `tabindex="-1"` to all other items. This method must be called prior to setting focus on a menu item.
   */
  setCurrentItem(item) {
    const items = this.getAllItems();
    items.forEach((i) => {
      i.setAttribute("tabindex", i === item ? "0" : "-1");
    });
  }
  render() {
    return ke`
      <slot
        @slotchange=${this.handleSlotChange}
        @click=${this.handleClick}
        @keydown=${this.handleKeyDown}
        @mousedown=${this.handleMouseDown}
      ></slot>
    `;
  }
};
WaMenu.shadowStyle = [size_default, menu_default];
__decorateClass([
  n({ reflect: true, initial: "medium" })
], WaMenu.prototype, "size", 2);
__decorateClass([
  e("slot")
], WaMenu.prototype, "defaultSlot", 2);
WaMenu = __decorateClass([
  t("wa-menu")
], WaMenu);

export {
  WaMenu
};
